"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useParams, useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { useSidebarStore } from "@/store/sidebar.store";
import { useSearchStore } from "@/store/search.store";
import { useAllCarts } from "@/hooks/use-cart";
import { useAuthStore } from "@/store/auth.store";
import { useToastStore } from "@/store/toast.store";
import { useAddressStore } from "@/store/address.store";
import { useAddressAutocomplete, useCreateAddress, useAddresses } from "@/hooks/use-addresses";
import { useGeolocation } from "@/hooks/use-geolocation";
import { authService } from "@/services/auth.service";
import { useTranslation } from "@/lib/i18n";
import { AddressPicker } from "@/components/address/address-picker";
import type { AddressAutocompleteResult, CustomerAddress } from "@/types";
import { ChevronDown, ChevronLeft, Clock4, Heart, LoaderCircle, MapPin, Navigation, Search, ShoppingCart, TextAlignJustify, X } from "lucide-react";
import { refreshForLocationChange } from "@/lib/location-refresh";

export function Header() {
  const pathname = usePathname();
  const params = useParams();
  const router = useRouter();
  const t = useTranslation();
  const { user } = useAuthStore();
  const { toggle: toggleSidebar } = useSidebarStore();
  const { query: searchQuery, setQuery: setSearchQuery } = useSearchStore();
  const queryClient = useQueryClient();
  const { data: allCarts } = useAllCarts();
  const storeIdParam = params?.storeId ? (Array.isArray(params.storeId) ? params.storeId[0] : params.storeId) : null;
  const cartStoreUlidParam = params?.storeUlid ? (Array.isArray(params.storeUlid) ? params.storeUlid[0] : params.storeUlid) : null;
  const onStorePage = !!pathname?.startsWith("/stores/") && !!storeIdParam;
  // Checkout gets a stripped-back header (logo only).
  const onCartPage = !!pathname?.startsWith("/cart");
  const onCartStorePage = !!cartStoreUlidParam;
  const activeStoreUlid = storeIdParam ?? cartStoreUlidParam;

  /**
   * Le badge compte TOUS les articles, tous paniers confondus.
   *
   * Il affichait auparavant les seuls articles du magasin consulté : sur une
   * fiche dont le panier est vide, il tombait donc à 0 alors que le client
   * avait quatre paniers en cours ailleurs. Un compteur de panier qui annonce
   * zéro pendant qu'on a des articles n'informe pas, il trompe.
   *
   * Le détail par magasin n'est pas perdu — c'est le rôle du panier flottant,
   * qui montre justement celui du magasin consulté.
   */
  const cartCount =
    allCarts?.reduce(
      (sum, c) => sum + (c.items?.reduce((n, i) => n + i.quantity, 0) ?? 0),
      0,
    ) ?? 0;

  const toast = useToastStore();
  const { selectedAddressUlid, setSelectedAddressUlid, guestAddress, setGuestAddress } = useAddressStore();
  // Gate pathname-dependent rendering until mounted — usePathname() can differ
  // between SSR and the client here, which would cause a hydration mismatch.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const [searchFocused, setSearchFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const [addressOpen, setAddressOpen] = useState(false);
  const [addressQuery, setAddressQuery] = useState("");
  const [debouncedAddressQuery, setDebouncedAddressQuery] = useState("");
  const addressRef = useRef<HTMLDivElement>(null);
  const guestAddressLabel = guestAddress ? guestAddress.formatted.split(",")[0] : null;

  // Debounce address autocomplete query
  useEffect(() => {
    const id = setTimeout(() => setDebouncedAddressQuery(addressQuery), 300);
    return () => clearTimeout(id);
  }, [addressQuery]);

  const { coords } = useGeolocation();
  const { data: autocompleteRaw, isFetching } = useAddressAutocomplete({
    query: debouncedAddressQuery,
    latitude: coords?.latitude,
    longitude: coords?.longitude,
    limit: 5,
  });
  // Normalise: the API may return a plain array or a paginated { data: [] } shape
  const autocompleteData = (autocompleteRaw as { data?: AddressAutocompleteResult[] } | undefined)?.data;
  const autocompleteResults: AddressAutocompleteResult[] | null = Array.isArray(autocompleteRaw)
    ? autocompleteRaw
    : Array.isArray(autocompleteData)
    ? autocompleteData
    : null;
  const { data: savedAddresses, isSuccess: addressesLoaded } = useAddresses();
  const lastSyncedLocRef = useRef<string>("");
  const createAddress = useCreateAddress();

  // Derive label for the header button
  const activeAddress = savedAddresses?.find((a) => a.ulid === selectedAddressUlid)
    ?? savedAddresses?.[0]
    ?? null;
  // Auto-select first saved address
  useEffect(() => {
    if (savedAddresses && savedAddresses.length > 0 && !selectedAddressUlid) {
      setSelectedAddressUlid(savedAddresses[0].ulid);
    }
  }, [savedAddresses, selectedAddressUlid]);

  // Close address dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (addressRef.current && !addressRef.current.contains(e.target as Node)) {
        setAddressOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // L'amorçage depuis les adresses enregistrées vit désormais dans
  // `useLocationTrust` : il doit s'appliquer partout, y compris avant que la
  // fenêtre de localisation décide de s'ouvrir, et pas seulement là où
  // l'en-tête est monté.

  // Keep the backend delivery location aligned with the active address (`coords`
  // already resolves to the chosen address, falling back to GPS). After syncing,
  // refetch the location-dependent queries so the store list matches the header.
  // Deduped by location so GPS drift doesn't spam updates.
  useEffect(() => {
    if (!user) return;
    const lat = coords?.latitude;
    const lng = coords?.longitude;
    if (lat == null || lng == null) return;

    const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
    if (lastSyncedLocRef.current === key) return;
    lastSyncedLocRef.current = key;

    authService
      .updateLocation({ latitude: lat, longitude: lng })
      // `finally` et non `then` : même si la synchronisation échoue, la
      // position locale a changé et l'écran doit suivre — sinon il resterait
      // sur l'ancienne zone sans que rien ne le signale.
      .finally(() => refreshForLocationChange(queryClient, true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, coords?.latitude, coords?.longitude]);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("recent-searches") ?? "[]");
      if (Array.isArray(stored)) setRecentSearches(stored);
    } catch { /* ignore */ }
  }, []);


  // Close search dropdown on outside click
  useEffect(() => {
    if (!searchFocused) return;
    function handler(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchFocused(false);
      }
    }
    const id = requestAnimationFrame(() => document.addEventListener("mousedown", handler));
    return () => { cancelAnimationFrame(id); document.removeEventListener("mousedown", handler); };
  }, [searchFocused]);

  function saveRecentSearch(term: string) {
    const trimmed = term.trim();
    if (!trimmed) return;
    const next = [trimmed, ...recentSearches.filter((s) => s !== trimmed)].slice(0, 8);
    setRecentSearches(next);
    localStorage.setItem("recent-searches", JSON.stringify(next));
  }

  function removeRecentSearch(term: string) {
    const next = recentSearches.filter((s) => s !== term);
    setRecentSearches(next);
    localStorage.setItem("recent-searches", JSON.stringify(next));
  }

  function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && searchQuery.trim()) {
      saveRecentSearch(searchQuery);
      setSearchFocused(false);
    }
    if (e.key === "Escape") {
      setSearchFocused(false);
    }
  }

  function pickRecentSearch(term: string) {
    setSearchQuery(term);
    setSearchFocused(false);
  }

  // AddressPicker owns its own step/query state and resets when it remounts,
  // which it does each time the dropdown is opened.
  const openDropdown = () => setAddressOpen(true);




  const refreshForAddress = useCallback(
    async (lat: number, lng: number) => {
      if (user) {
        try {
          await authService.updateLocation({ latitude: lat, longitude: lng });
        } catch {
          // location sync is best-effort
        }
      }
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      queryClient.invalidateQueries({ queryKey: ["carousels"] });
      queryClient.invalidateQueries({ queryKey: ["carousel-items"] });
      queryClient.invalidateQueries({ queryKey: ["banner"] });
      queryClient.invalidateQueries({ queryKey: ["store-detail"] });
      queryClient.invalidateQueries({ queryKey: ["app-categories"] });
      // Carts depend on the delivery address (a store may not serve the new zone).
      queryClient.invalidateQueries({ queryKey: ["carts"] });
    },
    [user, queryClient]
  );

  const handleSelectSaved = (addr: CustomerAddress) => {
    setSelectedAddressUlid(addr.ulid);
    setGuestAddress({ latitude: addr.latitude, longitude: addr.longitude, formatted: addr.name ?? addr.formatted, origin: "picked" });
    setAddressOpen(false);
    refreshForAddress(addr.latitude, addr.longitude);
    toast.show(t.header.addressChanged(addr.name ?? addr.formatted.split(",")[0]));
  };

  // Use the browser's current GPS position (reverse-geocoded to a real address).



  const handleCartClick = () => {
    // On a store detail page, jump straight into that store's checkout.
    if (pathname?.startsWith("/stores/") && params?.storeId) {
      const storeId = Array.isArray(params.storeId) ? params.storeId[0] : params.storeId;
      router.push(`/cart/${storeId}`);
    } else {
      router.push("/cart");
    }
  };

  // Address picker steps — shared by the desktop dropdown and the mobile sheet.
  const addressSteps = <AddressPicker onDone={() => setAddressOpen(false)} />;

  return (
    <>
    {/* ───────── Desktop header ───────── */}
    <header className="sticky top-0 z-30 hidden lg:flex h-16 items-center gap-4 border-b border-gray-200 dark:border-[#313131] bg-white dark:bg-[#1E1E1E] px-4 lg:px-6">
      {onCartPage ? (
        /* Checkout is a focused flow: no search, address or cart — just the
           sidebar toggle and a centered logo. The address is changed from the
           cart page itself. */
        <>
          <button
            onClick={toggleSidebar}
            className="hidden lg:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 dark:hover:bg-[#313131]"
            aria-label="Toggle sidebar"
          >
            <TextAlignJustify className="h-4 w-4" />
          </button>
          <Link href="/home" className="absolute left-1/2 -translate-x-1/2">
            <Image
              src="/fast-logo.svg"
              alt="Fast"
              width={80}
              height={32}
              className="h-7 w-auto fast-logo-svg"
            />
          </Link>
        </>
      ) : (
      <>
      {/* Sidebar toggle — desktop only */}
      <button
        onClick={toggleSidebar}
        className="hidden lg:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 dark:hover:bg-[#313131]"
        aria-label="Toggle sidebar"
      >
<TextAlignJustify className="h-4 w-4" />
      </button>

      {/* Logo */}
      <Link href="/home" className="shrink-0">
        <Image
          src="/fast-logo.svg"
          alt="Fast"
          width={80}
          height={32}
          className="h-7 w-auto fast-logo-svg"
        />
      </Link>

      {/* Search */}
      <div className="flex-1 max-w-xl mx-auto" ref={searchRef}>
        {/* Unified container — becomes a card when dropdown is open */}
        <div className={`relative transition-all ${
          searchFocused && !searchQuery && recentSearches.length > 0
            ? "rounded-t-2xl ring-1 ring-gray-200 dark:ring-[#313131] shadow-lg bg-white dark:bg-[#2A2A2A]"
            : "rounded-full bg-gray-100 dark:bg-[#2A2A2A] hover:bg-gray-200 dark:hover:bg-[#313131]"
        }`}>
 <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={mounted && pathname?.startsWith("/stores/") ? t.header.searchProduct : t.header.searchRestaurant}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onKeyDown={handleSearchKeyDown}
            className="w-full bg-transparent py-2.5 pl-10 pr-8 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none transition"
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(""); setSearchFocused(true); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={t.common.clear}
            >
<X className="h-4 w-4" />
            </button>
          )}

          {/* Recent searches — fused directly below the input, no gap */}
          {searchFocused && !searchQuery && recentSearches.length > 0 && (
            <div className="absolute left-0 top-full w-full z-50 bg-white dark:bg-[#2A2A2A] rounded-b-2xl ring-1 ring-gray-200 dark:ring-[#313131] shadow-lg border-t border-gray-100 dark:border-[#313131] py-2 overflow-hidden">
              <p className="px-4 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                {t.header.recentSearches}
              </p>
              {recentSearches.map((term) => (
                <div
                  key={term}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#313131] cursor-pointer group"
                  onMouseDown={(e) => { e.preventDefault(); pickRecentSearch(term); }}
                >
<Clock4 className="h-4 w-4 text-gray-400" />
                  <span className="flex-1 text-sm text-gray-700 dark:text-gray-200">{term}</span>
                  <button
                    onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); removeRecentSearch(term); }}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 transition-opacity"
                    aria-label={t.common.delete}
                  >
<X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-3 shrink-0">
        {/* Address dropdown */}
        <div className="relative block" ref={addressRef}>
          <button
            onClick={openDropdown}
            className="flex items-center gap-1.5 dark:hover:bg-[#313131] rounded-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
          >
<MapPin className="h-4 w-4 text-gray-400" />
            <span className="max-w-[160px] dark:text-gray-500  truncate">
              {guestAddressLabel
                ?? (activeAddress ? (activeAddress.name ?? activeAddress.formatted.split(",")[0]) : t.header.setAddress)}
            </span>
<ChevronDown className={`h-4 w-4 dark:text-gray-500 transition-transform ${addressOpen ? "rotate-180" : ""}`} />
          </button>

          {/* Dropdown panel */}
          {addressOpen && (
            <div className="absolute right-0 top-full mt-2 w-96 rounded-xl border border-gray-200 dark:border-[#313131]  bg-white dark:bg-[#2A2A2A] shadow-xl z-50 overflow-hidden">

              {addressSteps}
            </div>
          )}
        </div>


        <button
          onClick={handleCartClick}
          className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-bold text-secondary transition hover:bg-primary/90"
        >
<ShoppingCart className="h-4 w-4" />
          <span>{cartCount}</span>
        </button>
      </div>
      </>
      )}
    </header>

    {/* ───────── Mobile header (home only) ───────── */}
    {pathname === "/home" && (
    <header className="sticky top-0 z-30 flex lg:hidden flex-col gap-2.5 border-b border-gray-200 dark:border-[#313131] bg-white dark:bg-[#1E1E1E] px-4 py-2.5">
      {/* Row 1: address + favorites */}
      <div className="flex items-center justify-between gap-3">
        <button onClick={openDropdown} className="flex min-w-0 flex-col items-start text-left">
          <span className="text-xs text-gray-500 dark:text-gray-400 leading-none">{t.header.myCurrentAddress}</span>
          <span className="mt-1 flex items-center gap-1 max-w-[72vw]">
            <span className="truncate text-[15px] font-bold text-gray-900 dark:text-white">
              {guestAddressLabel
                ?? (activeAddress ? (activeAddress.name ?? activeAddress.formatted.split(",")[0]) : t.header.setAddress)}
            </span>
            <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400 transition-transform ${addressOpen ? "rotate-180" : ""}`} />
          </span>
        </button>
        <Link
          href="/saved-stores"
          aria-label={t.header.favorites}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-[#2A2A2A] text-gray-700 dark:text-gray-200 transition hover:bg-gray-200 dark:hover:bg-[#313131]"
        >
          <Heart className="h-5 w-5" />
        </Link>
      </div>

      {/* Row 2: search — taps through to the dedicated browse/search page */}
      <button
        onClick={() => router.push("/browse")}
        className="relative flex w-full items-center rounded-full bg-gray-100 dark:bg-[#2A2A2A] py-2.5 pl-10 pr-3 text-left"
        aria-label={t.header.searchPartner}
      >
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <span className="text-sm text-gray-500 dark:text-gray-400">{t.header.searchPartner}</span>
      </button>
    </header>
    )}

    {/* ───────── Mobile address view (full screen) ───────── */}
    {addressOpen && (
      <div
        className="lg:hidden fixed inset-0 z-[60] flex flex-col bg-white dark:bg-[#1E1E1E]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative flex items-center justify-center px-4 py-3">
          <button
            onClick={() => setAddressOpen(false)}
            className="absolute left-4 flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] transition"
            aria-label={t.common.back}
          >
            <ChevronLeft className="h-5 w-5 text-gray-800 dark:text-gray-200" strokeWidth={2} />
          </button>
          <h2 className="text-base font-bold text-gray-900 dark:text-white">{t.header.enterAddress}</h2>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-8">
          {/* Search input */}
          <div className="relative mt-2">
            <input
              data-mobile-addr
              autoFocus
              value={addressQuery}
              onChange={(e) => setAddressQuery(e.target.value)}
              placeholder={t.header.enterAddress}
              className="w-full rounded-full bg-gray-100 dark:bg-[#2A2A2A] px-5 py-3.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none"
            />
            {isFetching && (
              <LoaderCircle className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
            )}
          </div>

          {addressQuery.trim().length >= 2 ? (
            /* ── Search results ── */
            <div className="mt-3 divide-y divide-gray-100 dark:divide-[#313131]">
              {autocompleteResults && autocompleteResults.length > 0 ? (
                autocompleteResults.map((r) => (
                  <button
                    key={r.place_id}
                    onClick={async () => {
                      // Set the active address first so it applies immediately —
                      // saving it server-side (logged-in users) is best-effort.
                      setGuestAddress({ latitude: r.latitude, longitude: r.longitude, formatted: r.formatted, origin: "picked" });
                      lastSyncedLocRef.current = "";
                      refreshForAddress(r.latitude, r.longitude);
                      toast.show(t.header.addressChanged(r.formatted.split(",")[0]));
                      setAddressOpen(false);
                      if (user) {
                        try {
                          const created = await createAddress.mutateAsync({ formatted: r.formatted, latitude: r.latitude, longitude: r.longitude });
                          setSelectedAddressUlid(created.ulid);
                        } catch { /* ignore */ }
                      }
                    }}
                    className="flex w-full items-start gap-3 py-3.5 text-left"
                  >
                    <MapPin className="mt-0.5 h-5 w-5 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-800 dark:text-gray-200 leading-snug">{r.formatted}</span>
                  </button>
                ))
              ) : (
                !isFetching && <p className="py-4 text-sm text-gray-400 dark:text-gray-500">{t.common.noResults}</p>
              )}
            </div>
          ) : (
            <>
              {/* Use my actual address */}
              {(guestAddress?.formatted || activeAddress) && (
                <button
                  onClick={() => {
                    const lat = guestAddress?.latitude ?? coords?.latitude ?? activeAddress?.latitude;
                    const lng = guestAddress?.longitude ?? coords?.longitude ?? activeAddress?.longitude;
                    if (lat != null && lng != null) refreshForAddress(lat, lng);
                    setAddressOpen(false);
                  }}
                  className="mt-5 flex w-full items-center gap-3 text-left"
                >
                  <Navigation className="h-6 w-6 shrink-0 text-secondary dark:text-primary" />
                  <div className="min-w-0">
                    <p className="text-sm leading-tight text-gray-500 dark:text-gray-400">{t.header.useCurrentLocation}</p>
                    <p className="truncate text-[15px] font-bold text-gray-900 dark:text-white">
                      {guestAddress?.formatted?.split(",").slice(0, 2).join(",") ?? activeAddress?.formatted.split(",").slice(0, 2).join(",")}
                    </p>
                  </div>
                </button>
              )}

              <div className="my-5 border-t border-gray-100 dark:border-[#313131]" />

              {/* Favorites */}
              <h3 className="text-xl font-extrabold text-gray-900 dark:text-white">{t.header.favoriteAddresses}</h3>
              {savedAddresses && savedAddresses.length > 0 ? (
                <div className="mt-2 divide-y divide-gray-100 dark:divide-[#313131]">
                  {savedAddresses.map((addr) => (
                    <button
                      key={addr.ulid}
                      onClick={() => handleSelectSaved(addr)}
                      className="flex w-full items-center gap-3 py-3.5 text-left"
                    >
                      <MapPin className="h-5 w-5 shrink-0 text-gray-400" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[15px] font-semibold text-gray-900 dark:text-white">{addr.name ?? addr.formatted.split(",")[0]}</p>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{addr.formatted}</p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <p className="mt-2 text-[15px] text-gray-600 dark:text-gray-400">{t.header.noFavoriteAddress}</p>
                  <button
                    onClick={() => {
                      document.querySelector<HTMLInputElement>("[data-mobile-addr]")?.focus();
                    }}
                    className="mt-4 w-full rounded-2xl bg-primary py-4 text-base font-bold text-secondary transition hover:bg-primary/90"
                  >
                    {t.header.addToFavorites}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    )}
    </>
  );
}
